import { useState } from "react";
import { Trash2, Loader2, Mail, Phone, Globe, FileSpreadsheet, FileText, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBulkSelect } from "@/hooks/useBulkSelect";

export default function AdLeadsPanel({ leads, setLeads, loadingLeads, isAdmin }) {
  const [exportingFormat, setExportingFormat] = useState(null);
  const bulkSelect = useBulkSelect();

  async function exportLeads(format) {
    setExportingFormat(format);
    try {
      const res = await api.get(`/admin/ad-leads/export?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ad-leads.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExportingFormat(null);
    }
  }

  async function deleteLead(id) {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/ad-leads/${id}`);
      setLeads((items) => items.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  async function bulkDeleteLeads() {
    const ids = [...bulkSelect.selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected lead(s)? This cannot be undone.`)) return;
    try {
      await api.post("/admin/ad-leads/bulk-delete", { ids });
      setLeads((items) => items.filter((x) => !ids.includes(x.id)));
      bulkSelect.clear();
      toast.success(`Deleted ${ids.length} lead(s)`);
    } catch {
      toast.error("Bulk delete failed");
    }
  }

  return (
    <div className="space-y-3" data-testid="admin-ad-leads-panel">
      <div className="flex justify-end gap-2 mb-1">
        <button
          onClick={() => exportLeads("xlsx")}
          disabled={exportingFormat !== null || leads.length === 0}
          className="btn-outline-gold text-sm py-2 px-4 disabled:opacity-40"
          data-testid="admin-ad-leads-export-xlsx"
        >
          {exportingFormat === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
          Export Excel
        </button>
        <button
          onClick={() => exportLeads("pdf")}
          disabled={exportingFormat !== null || leads.length === 0}
          className="btn-outline-gold text-sm py-2 px-4 disabled:opacity-40"
          data-testid="admin-ad-leads-export-pdf"
        >
          {exportingFormat === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          Export PDF
        </button>
      </div>

      {loadingLeads && <div className="text-white/55 text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
      {!loadingLeads && leads.length === 0 && (
        <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-ad-leads">
          No ad leads yet. They'll appear here as visitors submit the campaign landing page.
        </div>
      )}

      {isAdmin && !loadingLeads && leads.length > 0 && (
        <div className="flex items-center gap-3 mb-1" data-testid="admin-ad-leads-bulk-bar">
          <label className="flex items-center gap-2 text-xs text-white/55 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[var(--gold)] cursor-pointer"
              checked={bulkSelect.isAllSelected(leads.map((l) => l.id))}
              onChange={() => bulkSelect.toggleAll(leads.map((l) => l.id))}
              data-testid="admin-ad-leads-select-all"
            />
            Select all
          </label>
          {bulkSelect.selected.size > 0 && (
            <button
              onClick={bulkDeleteLeads}
              className="text-xs px-3 py-1.5 rounded-full border border-red-400/30 text-red-400 hover:bg-red-500/10 transition inline-flex items-center gap-1.5"
              data-testid="admin-ad-leads-bulk-delete"
            >
              <Trash2 size={12} /> Delete Selected ({bulkSelect.selected.size})
            </button>
          )}
        </div>
      )}

      {leads.map((lead) => (
        <div key={lead.id} className="card-dark p-5 flex gap-3" data-testid={`ad-lead-row-${lead.id}`}>
          {isAdmin && (
            <input
              type="checkbox"
              className="w-4 h-4 mt-1 accent-[var(--gold)] cursor-pointer shrink-0"
              checked={bulkSelect.selected.has(lead.id)}
              onChange={() => bulkSelect.toggle(lead.id)}
              data-testid={`ad-lead-select-${lead.id}`}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="font-semibold text-white">{lead.name}</h3>
                <div className="flex flex-wrap items-center gap-3 text-xs text-white/55 mt-1">
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[var(--gold)]">
                    <Mail size={12} /> {lead.email}
                  </a>
                  {lead.phone && <span className="flex items-center gap-1"><Phone size={12} /> {lead.phone}</span>}
                  {lead.destination_country && (
                    <span className="flex items-center gap-1"><Globe size={12} /> {lead.destination_country.toUpperCase()}</span>
                  )}
                  {lead.service_type && (
                    <span className="px-2 py-0.5 rounded-full border border-[var(--gold)]/25 text-[var(--gold)]">
                      {lead.service_type}
                    </span>
                  )}
                  {lead.source && (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/15">
                      <Megaphone size={12} /> {lead.source}
                    </span>
                  )}
                </div>
                {(lead.country_of_residence || lead.highest_qualification || lead.field_of_study || lead.preferred_intake || lead.tuition_budget) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/55 mt-2">
                    {lead.country_of_residence && <span>Resides in: <span className="text-white/80">{lead.country_of_residence}</span></span>}
                    {lead.highest_qualification && <span>Qualification: <span className="text-white/80">{lead.highest_qualification}</span></span>}
                    {lead.field_of_study && <span>Field of study: <span className="text-white/80">{lead.field_of_study}</span></span>}
                    {lead.preferred_intake && <span>Intake: <span className="text-white/80">{lead.preferred_intake}</span></span>}
                    {lead.tuition_budget && <span>Budget: <span className="text-white/80">{lead.tuition_budget}</span></span>}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/45">{new Date(lead.created_at).toLocaleString()}</span>
                {isAdmin && (
                  <button
                    onClick={() => deleteLead(lead.id)}
                    className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
                    title="Delete"
                    data-testid={`ad-lead-delete-${lead.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            {lead.message && <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap">{lead.message}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
